import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import libFloat from "../libFloat";
import { formatInTimeZone } from "date-fns-tz";

// Azul primário do sistema: #3c5f97
const PRIMARY = [60, 95, 151];
const GRAY_LABEL = [107, 114, 128];
const GRAY_DARK = [31, 41, 55];
const GRAY_BORDER = [196, 194, 194];

const fmt = (val) => libFloat.strToCurrency(val ?? 0);

const fmtDate = (val) => {
	if (!val) return "—";
	try {
		return formatInTimeZone(new Date(val), "UTC", "dd/MM/yyyy");
	} catch {
		return "—";
	}
};

const fmtDateTime = (val) => {
	if (!val) return "—";
	try {
		return formatInTimeZone(new Date(val), "UTC", "dd/MM/yyyy HH:mm");
	} catch {
		return "—";
	}
};

function sectionHeader(doc, label, y, pageWidth, margin) {
	doc.setFont("helvetica", "bold");
	doc.setFontSize(10);
	doc.setTextColor(...PRIMARY);
	doc.text(label, margin, y);
	doc.setDrawColor(...PRIMARY);
	doc.setLineWidth(0.4);
	doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
	return y + 6;
}

function twoColumns(doc, rows, y, margin, pageWidth) {
	const xL = margin;
	const xR = pageWidth / 2 + 4;
	const colW = pageWidth / 2 - margin - 4;

	rows.forEach(([labelL, valueL, labelR, valueR]) => {
		doc.setFont("helvetica", "normal");
		doc.setFontSize(8);
		doc.setTextColor(...GRAY_LABEL);
		doc.text(String(labelL ?? ""), xL, y);
		if (labelR != null) doc.text(String(labelR), xR, y);
		y += 4;

		doc.setFont("helvetica", "bold");
		doc.setFontSize(9);
		doc.setTextColor(...GRAY_DARK);
		const vL = String(valueL ?? "—");
		const vR = labelR != null ? String(valueR ?? "—") : null;
		doc.text(doc.splitTextToSize(vL, colW)[0], xL, y);
		if (vR != null) doc.text(doc.splitTextToSize(vR, colW)[0], xR, y);
		y += 6;
	});

	return y;
}

function desenharPaginaVenda(
	doc,
	{
		saleData,
		saleHeader = null,
		installments = [],
		products = [],
		complements = [],
		clientData = null,
		empresa = "SZ Sistemas",
	},
) {
	const pageWidth = doc.internal.pageSize.getWidth();
	const margin = 14;

	// ─── HEADER ─────────────────────────────────────────────────────────────
	const headerH = 28;
	doc.setFillColor(...PRIMARY);
	doc.rect(0, 0, pageWidth, headerH, "F");

	doc.setTextColor(255, 255, 255);
	doc.setFont("helvetica", "bold");
	doc.setFontSize(14);
	doc.text("RELATÓRIO DE VENDA", margin, 13);

	doc.setFont("helvetica", "normal");
	doc.setFontSize(8.5);
	doc.text(String(empresa), margin, 21);

	const idVenda = saleData.id_venda ?? installments[0]?.id_venda ?? "—";
	const rightX = pageWidth - margin;
	doc.setFontSize(8.5);
	doc.text(`Venda #${idVenda}`, rightX, 11, { align: "right" });
	doc.text(`Data: ${fmtDateTime(saleData.datavenda ?? installments[0]?.datavenda)}`, rightX, 17, { align: "right" });
	doc.text(`Emissão: ${formatInTimeZone(new Date(), "America/Sao_Paulo", "dd/MM/yyyy HH:mm")}`, rightX, 23, {
		align: "right",
	});

	let y = headerH + 8;

	// ─── DADOS DA VENDA ──────────────────────────────────────────────────────
	y = sectionHeader(doc, "DADOS DA VENDA", y, pageWidth, margin);

	const num = (v) => Number(v ?? 0);

	// desconto real fica rateado em venda_item.desconto — conta_entrada.desconto (parcela) não é preenchido pelo backend
	const descontoItens = products.reduce((s, item) => s + num(item.desconto), 0);
	// valor da parcela já é líquido (desconto e acréscimo aplicados), não bruto
	const somaParcelas = installments.reduce((s, p) => s + num(p.valor), 0);

	const totalAcrescimo = num(saleHeader?.acrescimo) + num(saleHeader?.taxaservico) + num(saleHeader?.couvert);
	const totalEntrega = num(saleHeader?.valorentrega);
	const totalDesconto = num(saleHeader?.desconto) || descontoItens;
	const totalLiquido = num(saleHeader?.valor) || somaParcelas || num(saleData.soma ?? saleData.valor);
	const totalBruto =
		num(saleHeader?.valorvenda) || totalLiquido + totalDesconto - totalAcrescimo - totalEntrega;

	const vendaRows = [
		["Código da Venda", idVenda, "Data da Venda", fmtDateTime(saleData.datavenda ?? installments[0]?.datavenda)],
	];
	if (totalDesconto > 0) {
		vendaRows.push(["Desconto Total", fmt(totalDesconto), null, null]);
	}

	y = twoColumns(doc, vendaRows, y, margin, pageWidth);
	y += 2;

	// ─── DADOS DO CLIENTE ────────────────────────────────────────────────────
	y = sectionHeader(doc, "DADOS DO CLIENTE", y, pageWidth, margin);

	const nome = clientData?.nome ?? saleData.nome ?? installments[0]?.nome ?? "—";
	const cpf = clientData?.cpf ?? "—";
	const telefone = clientData?.telefone_1 ?? clientData?.telefone_2 ?? "—";
	const email = clientData?.email ?? "—";
	const cidade = clientData?.nomecidade ?? "—";
	const uf = clientData?.uf ?? "";

	const clienteRows = [
		["Nome", nome, "CPF / CNPJ", cpf],
		["Telefone", telefone, "E-mail", email],
	];
	if (cidade !== "—") {
		clienteRows.push(["Cidade / UF", `${cidade}${uf ? " / " + uf : ""}`, null, null]);
	}

	y = twoColumns(doc, clienteRows, y, margin, pageWidth);
	y += 2;

	// ─── PRODUTOS ────────────────────────────────────────────────────────────
	if (products.length > 0) {
		y = sectionHeader(doc, "PRODUTOS", y, pageWidth, margin);

		// Monta linhas planas com metadado de tipo para estilização
		const rows = [];
		const rowTypes = []; // 'simple' | 'item-header' | 'complement'

		const temDescontoItem = products.some((item) => num(item.desconto) > 0);

		let seq = 1;
		for (const item of products) {
			const itemComps = complements.filter((c) => c.id_venda_item === item.id_venda_item);

			if (itemComps.length > 0) {
				const isGrade = itemComps.some((c) => String(c.tipo ?? "").toUpperCase() === "GRADE");
				const compTotal = itemComps.reduce((s, c) => s + num(c.valor), 0);
				const itemTotal = isGrade ? compTotal * Number(item.qtde ?? 1) : num(item.valor) + compTotal;

				rows.push([
					seq,
					item.nome || item.descricao || "—",
					`${item.qtde ?? 1}x`,
					isGrade ? "" : fmt(item.valor_unit),
					...(temDescontoItem ? [num(item.desconto) > 0 ? fmt(item.desconto) : "—"] : []),
					fmt(itemTotal),
				]);
				rowTypes.push("item-header");

				// Linhas dos complementos
				for (const comp of itemComps) {
					const isSubitemIncluso =
						String(comp.tipo ?? "").toUpperCase() === "SUBITEM" && num(comp.valor) === 0;
					rows.push([
						"",
						`    ${comp.nome || "—"}`,
						String(comp.qtde ?? 1),
						isSubitemIncluso ? "incluso" : fmt(comp.valorunit),
						...(temDescontoItem ? [""] : []),
						isSubitemIncluso ? "—" : fmt(comp.valor),
					]);
					rowTypes.push("complement");
				}
			} else {
				// Produto simples sem complementos
				rows.push([
					seq,
					item.nome || item.descricao || "—",
					String(item.qtde ?? 1),
					fmt(item.valor_unit),
					...(temDescontoItem ? [num(item.desconto) > 0 ? fmt(item.desconto) : "—"] : []),
					fmt(item.valor),
				]);
				rowTypes.push("simple");
			}
			seq++;
		}

		autoTable(doc, {
			startY: y,
			head: [
				temDescontoItem
					? ["#", "Produto / Complemento", "Qtde", "Valor Unit.", "Desconto", "Total"]
					: ["#", "Produto / Complemento", "Qtde", "Valor Unit.", "Total"],
			],
			body: rows,
			styles: { fontSize: 8.5, cellPadding: 2 },
			headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: "bold" },
			columnStyles: temDescontoItem
				? {
						0: { halign: "center", cellWidth: 8 },
						2: { halign: "center", cellWidth: 14 },
						3: { halign: "right", cellWidth: 26 },
						4: { halign: "right", cellWidth: 24 },
						5: { halign: "right", cellWidth: 26 },
					}
				: {
						0: { halign: "center", cellWidth: 8 },
						2: { halign: "center", cellWidth: 14 },
						3: { halign: "right", cellWidth: 28 },
						4: { halign: "right", cellWidth: 28 },
					},
			didParseCell: (data) => {
				if (data.section !== "body") return;
				const type = rowTypes[data.row.index];

				if (type === "item-header") {
					data.cell.styles.fillColor = [235, 240, 250];
					data.cell.styles.fontStyle = "bold";
					data.cell.styles.textColor = PRIMARY;
				} else if (type === "complement") {
					data.cell.styles.fillColor = [249, 250, 252];
					data.cell.styles.fontSize = 8;
					data.cell.styles.textColor = [75, 85, 99];
				} else {
					// simple — alterna cor a cada produto (usando seq par/ímpar)
					const itemIdx = rows.slice(0, data.row.index).filter((_, i) => rowTypes[i] === "simple").length;
					data.cell.styles.fillColor = itemIdx % 2 === 0 ? [255, 255, 255] : [245, 247, 250];
				}
			},
			theme: "grid",
			margin: { left: margin, right: margin },
		});

		y = doc.lastAutoTable.finalY + 6;
	}

	// ─── RESUMO FINANCEIRO ───────────────────────────────────────────────────
	y = sectionHeader(doc, "RESUMO FINANCEIRO", y, pageWidth, margin);

	const finRows = [
		[
			"Valor Bruto",
			fmt(totalBruto),
			totalDesconto > 0 ? "(-) Desconto" : null,
			totalDesconto > 0 ? fmt(totalDesconto) : null,
		],
	];

	const adicionais = [];
	if (totalAcrescimo > 0) adicionais.push(["(+) Acréscimos / Taxas", fmt(totalAcrescimo)]);
	if (totalEntrega > 0) adicionais.push(["(+) Entrega", fmt(totalEntrega)]);

	for (let i = 0; i < adicionais.length; i += 2) {
		const [labelL, valueL] = adicionais[i];
		const direita = adicionais[i + 1];
		finRows.push([labelL, valueL, direita ? direita[0] : null, direita ? direita[1] : null]);
	}

	if (totalDesconto > 0 && adicionais.length === 0) {
		finRows.push(["Valor Líquido", fmt(totalLiquido), null, null]);
	}

	y = twoColumns(doc, finRows, y, margin, pageWidth);

	// Total destacado
	const totalLineY = y + 1;
	doc.setFillColor(240, 244, 251);
	doc.rect(margin, totalLineY - 5, pageWidth - margin * 2, 10, "F");
	doc.setFont("helvetica", "bold");
	doc.setFontSize(10);
	doc.setTextColor(...PRIMARY);
	doc.text("TOTAL DA VENDA:", margin + 3, totalLineY + 1.5);
	doc.setFontSize(11);
	doc.text(fmt(totalLiquido), pageWidth - margin - 3, totalLineY + 1.5, { align: "right" });
	y = totalLineY + 10;

	// ─── PARCELAS ────────────────────────────────────────────────────────────
	y = sectionHeader(doc, "PARCELAS", y, pageWidth, margin);

	const headParcela = ["Parcela", "Vencimento", "Forma Pgto", "Status", "Dt. Pagamento", "Valor"];

	const bodyParcela = installments.map((p) => {
		const isPending = p.ativo === "SIM" || p.ativo === true || p.ativo === 1;
		return [
			`${p.parcela ?? "—"}/${p.parcela_total ?? "—"}`,
			fmtDate(p.data),
			p.forma || "—",
			isPending ? "Pendente" : "Pago",
			fmtDate(p.datapgto),
			fmt(p.valor),
		];
	});

	const colStylesParcela = {
		0: { halign: "center", cellWidth: 18 },
		1: { halign: "center", cellWidth: 24 },
		3: { halign: "center", cellWidth: 20 },
		4: { halign: "center", cellWidth: 24 },
		5: { halign: "right", cellWidth: 24 },
	};

	autoTable(doc, {
		startY: y,
		head: [headParcela],
		body: bodyParcela,
		styles: { fontSize: 8.5, cellPadding: 2 },
		headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: "bold" },
		alternateRowStyles: { fillColor: [245, 247, 250] },
		columnStyles: colStylesParcela,
		didParseCell: (data) => {
			if (data.column.index === 3 && data.section === "body") {
				const isPending = data.cell.raw === "Pendente";
				data.cell.styles.textColor = isPending ? [185, 28, 28] : [22, 101, 52];
				data.cell.styles.fontStyle = "bold";
			}
		},
		theme: "grid",
		margin: { left: margin, right: margin },
	});

	// ─── RODAPÉ ──────────────────────────────────────────────────────────────
	const footerY = doc.internal.pageSize.getHeight() - 8;
	doc.setFontSize(7.5);
	doc.setTextColor(...GRAY_BORDER);
	doc.setFont("helvetica", "normal");
	doc.text("Relatório gerado pelo sistema SZ Sistemas.", margin, footerY);
	doc.text(`Venda #${idVenda}`, pageWidth - margin, footerY, { align: "right" });
}

export function gerarPdfVendaCompleta(vendaProps) {
	const doc = new jsPDF("p", "mm", "a4");
	desenharPaginaVenda(doc, vendaProps);

	const blob = doc.output("blob");
	const url = URL.createObjectURL(blob);
	window.open(url, "_blank");
}

// Gera um único PDF com uma venda completa (dados, cliente, produtos e parcelas) por página
export function gerarPdfVendasMultiplas(vendas = []) {
	if (vendas.length === 0) return;

	const doc = new jsPDF("p", "mm", "a4");

	vendas.forEach((vendaProps, index) => {
		if (index > 0) doc.addPage();
		desenharPaginaVenda(doc, vendaProps);
	});

	const blob = doc.output("blob");
	const url = URL.createObjectURL(blob);
	window.open(url, "_blank");
}
